import { DragDropModule } from '@angular/cdk/drag-drop';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import localeFrBE from '@angular/common/locales/fr-BE';
import localeNlBE from '@angular/common/locales/nl-BE';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, Router, withPreloading, withRouterConfig } from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { BlockUIModule } from 'ng-block-ui';
import { NgcCookieConsentConfig, provideNgcCookieConsent } from 'ngx-cookieconsent';
import { DropzoneModule } from 'ngx-dropzone-wrapper';
import { hoverPrefetchProviders, HoverPreloadStrategy } from 'ngx-hover-preload';
import { NgxPermissionsModule } from 'ngx-permissions';

import { environment } from 'src/environments/environment';
import { HttpErrorInterceptor } from './core/interceptors/http-error-interceptor';
import { TokenInterceptor } from './core/interceptors/token-interceptor';
import { AUTH_API_BASE_URL } from './core/services/api/auth.service';
import { API_BASE_URL as CORE_URL } from './core/services/api/core.service';
import { API_BASE_URL as FRAMEWORK_URL } from './core/services/api/framework.service';
import { AuthService } from './core/services/auth.service';
import { EagleNavService } from './core/services/eagle-nav.service';
import { LoggerService } from './core/services/logger.service';
import AppRoutes from './routes';
import { NavService } from './shared/services/nav.service';
import { TableServices } from './core/components/table/table.services';
import { toTransient } from './core/utils/service.utils';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/');
}

export function getApiBaseUrl(): string {
  return environment.api;
}

export function getApiCoreBaseUrl(): string {
  return environment.core.api;
}

export function initializeLogger(logger: LoggerService) {
  return () => logger.init();
}

registerLocaleData(localeNlBE, 'nl-be');
registerLocaleData(localeFrBE, 'fr-be');
registerLocaleData(localeEs, 'es-es');

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: (new URL(environment.www)).hostname
  },
  position: "bottom-right",
  theme: "block",
  palette: {
    popup: {
      background: "#4e5155",
      text: "#ffffff",
      link: "#28c3d7"
    },
    button: {
      background: "#f9b233",
      text: "#644714",
      border: "transparent"
    }
  },
  type: "opt-in",
  content: {
    message: "This website uses cookies. Essential and functional cookies are necessary for the proper functioning of the website and cannot be refused. Other cookies are used for statistical purposes (analytical cookies) and are only placed after you have made a choice.",
    dismiss: "",
    deny: "Verdergaan met analytische cookies",
    link: "Learn more",
    href: "https://www.eaglebe.com/privacy/",
    policy: "Cookie Policy",
    allow: "Begrepen",
    privacy: "Privacy Policy",
    accessibility: "Accessibility Policy",
    accessibilityHref: "#"
  },
  elements: {
    deny: '<a aria-label="deny cookies" tabindex="0" class="cc-btn cc-deny" style="white-space: normal">{{deny}}</a>',
    compliancelinks: `
      <p class="mb-0 mt-2 text-center"><a class="link-light text-underline" href="{{href}}">{{privacy}}</a> - <a class="link-light text-underline" href="{{accessibilityHref}}">{{accessibility}}</a></p>
    `
  },
  layout: "custom-layout",
  layouts: {
    "custom-layout": "{{messagelink}}{{compliance}}{{compliancelinks}}"
  }
};

export default {
  providers: [
    importProvidersFrom(
      BrowserModule,
      NgxPermissionsModule.forRoot(),
      DropzoneModule,
      DragDropModule,
      BlockUIModule.forRoot(),
      CalendarModule.forRoot({
        provide: DateAdapter,
        useFactory: adapterFactory
      })
    ),
    provideAngularSvgIcon(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    provideNgcCookieConsent(cookieConfig),
    //{ provide: APP_INITIALIZER, deps: [AuthService], useFactory: () => null },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: CORE_URL, useFactory: getApiCoreBaseUrl },
    { provide: FRAMEWORK_URL, useFactory: getApiBaseUrl },
    { provide: AUTH_API_BASE_URL, useFactory: getApiBaseUrl },
    { provide: NavService, useFactory: (router: Router, authService: AuthService, translate: TranslateService) => { return new EagleNavService(router, authService, translate); }, deps: [Router, AuthService, TranslateService] },
    { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false, showError: true } },
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideRouter(AppRoutes, withRouterConfig({ paramsInheritanceStrategy: 'always' }), withPreloading(PreloadAllModules)),
    provideAnimations(),
    ...toTransient(TableServices)
  ]
} satisfies ApplicationConfig;